import { format, formatDistanceToNow, isValid } from "date-fns";

export function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (!isValid(date)) return "—";
  return format(date, "MMM d, h:mm a");
}

export function formatDay(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (!isValid(date)) return "—";
  return format(date, "MMM d");
}

export function formatRelative(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (!isValid(date)) return "Never";
  return formatDistanceToNow(date, { addSuffix: true });
}

export function formatDueLabel(value) {
  if (!value) return "Unscheduled";
  const date = new Date(value);
  if (!isValid(date)) return "Unscheduled";
  const diffMs = date.getTime() - Date.now();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) {
    const ago = Math.abs(diffHours);
    if (ago < 24) return `Overdue ${ago}h`;
    return `Overdue ${Math.abs(diffDays)}d`;
  }
  if (diffHours < 1) return "Due now";
  if (diffHours < 24) return `In ${diffHours}h`;
  if (diffDays < 14) return `In ${diffDays}d`;
  return format(date, "MMM d");
}

export function primaryContactEmail(lead) {
  if (!lead) return null;
  return (
    lead.primary_email ||
    lead.emails?.[0]?.address ||
    lead.people?.find((p) => p.email)?.email ||
    null
  );
}

export function stageTone(stage) {
  switch (stage) {
    case "replied":
    case "won":
    case "meeting_booked":
      return "success";
    case "qualified":
      return "warning";
    case "contacted":
    case "queued":
      return "info";
    case "lost":
    case "do_not_contact":
      return "danger";
    default:
      return "default";
  }
}

export function buildThreadMessages(detail) {
  if (!detail?.lead) return [];

  const fromActivities = (detail.activities || []).map((activity) => ({
    direction: activity.direction,
    from: activity.from,
    to: activity.to,
    subject: activity.subject,
    html: activity.html,
    text: activity.text,
    timestamp: activity.occurredAt,
    threadId: activity.threadId,
    messageId: activity.providerMessageId || activity._id,
  }));

  const fromLegacy = (detail.lead.messages || []).map((message) => ({
    ...message,
    timestamp: message.timestamp,
  }));

  const seen = new Set();
  return [...fromActivities, ...fromLegacy]
    .filter((message) => {
      const key =
        message.messageId ||
        `${message.direction}-${message.threadId}-${message.subject}-${message.timestamp}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

export function formatClockTime(hour, minute = 0) {
  const h = Number.isFinite(hour) ? hour : 9;
  const m = Number.isFinite(minute) ? minute : 0;
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return format(date, "h:mm a");
}

// Human-readable cadence label for a sequence step, e.g.
//   "Day 0 · 9:00 AM"
//   "+3 business days · 10:30 AM"
//   "+7 days · 9:00 AM"
export function formatStepCadence(step, { isFirst = false } = {}) {
  if (!step) return "";
  const delay = Number.isFinite(step.delayDays) ? step.delayDays : 0;
  const unit = step.businessDaysOnly ? "business day" : "day";
  const offset =
    isFirst && delay === 0
      ? "Day 0"
      : delay === 1
      ? `+1 ${unit}`
      : `+${delay} ${unit}s`;
  const time = formatClockTime(step.sendAtHour, step.sendAtMinute);
  return `${offset} · ${time}`;
}

export function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}
